import { z } from '@simplisafe/ewok'
import * as A from 'fp-ts/lib/Array'
import * as O from 'fp-ts/lib/Option'
import * as R from 'fp-ts/lib/Record'
import { pipe } from 'fp-ts/lib/function'

/**
 * Converts an unknown value to an object with unknown values.
 *
 * WARNING: throws an error if the input value is not an object.
 *
 * @example
 * const unknownValue: unknown = { name: 'John', age: 30 };
 * const convertedObject = unsafeConvertUnknownToObject(unknownValue);
 * console.log(convertedObject); // { name: 'John', age: 30 }
 */
export const unsafeConvertUnknownToObject = (
  t: unknown
): Record<string, unknown> => z.object({}).passthrough().parse(t)

/**
 * Retrieves the first item from a record of unknown values.
 *
 * @example
 * const record = { items: [1, 2, 3] };
 * const firstItem = getFirstItem(record); // Returns Some(1)
 */
export const getFirstItem = (t: Record<string, unknown>): O.Option<unknown> => {
  const items = R.lookup('items', t)
  return O.isSome(items)
    ? pipe(
        items,
        O.chain(i => (Array.isArray(i) ? A.head(i) : O.of(i)))
      )
    : O.of(t)
}

/**
 * Retrieves the first item from a Contentful result based on a query key.
 */
export const getContentfulResult = <T>(
  result: T,
  queryKey: string,
  isCollection: boolean
) => {
  return pipe(
    O.fromNullable(result),
    O.chain(R.lookup(queryKey)),
    O.chain(query => O.tryCatch(() => unsafeConvertUnknownToObject(query))),
    O.chain(x => (isCollection ? R.lookup('items')(x) : getFirstItem(x)))
  )
}

export const toError = (e: unknown) =>
  e instanceof Error ? e : Error(JSON.stringify(e))

export const makeHeaders = <T extends { readonly headers: Headers }>(
  response?: T
) => response?.headers || new Headers()
